/**
 * Account sync rows shared by the Dashboard header and the Sync Widget.
 *
 * Both surfaces used to derive "how many accounts need attention" on their own:
 * the header read the readiness manifest only, while the widget overlaid live
 * import sessions on top of it. The two answers disagreed whenever a session
 * was in flight, which is how one screenshot could show "2 account(s) need
 * attention" and another "3" at the same moment.
 *
 * The row list below is the single source of truth for that question. Both
 * surfaces build it from the same two inputs (readiness manifest + live import
 * sessions) and count the same rows, so the numbers cannot drift apart.
 *
 * Readiness accounts are filtered to the dashboard ad platforms, matching what
 * the header has always reported. Import sessions are not platform-filtered:
 * they are transient state for an import the user just started, and the widget
 * has to show its progress. Since both surfaces consume this same list, that
 * choice cannot reintroduce a mismatch.
 */

import { DASHBOARD_AD_PLATFORMS, PLATFORM_LABEL } from '@/lib/ad-platforms'
import {
  accountDisplayIdentity,
  aggregateAccountStatus,
  type AccountReadiness,
  type ReadinessVisualState,
} from '@/lib/data-readiness'
import { integrationAccountKey } from '@/lib/integration-account-key'
import { buildBrandUrl } from '@/lib/routes'
import type { SyncImportSession } from '@/stores/sync-widget-store'

export interface AccountRowModel {
  key: string
  name: string
  secondaryName?: string | null
  platform: string
  /** Present when the account is covered by the readiness manifest (L2). */
  readiness?: AccountReadiness
  /** Present while an import session (L1) still tracks the account. */
  sessionStatus?: 'pending' | 'syncing' | 'done' | 'error'
  campaignCount?: number
  bindingError?: string
}

/** Readiness rows the dashboard reports on (ad platforms only). */
export function dashboardScopedAccounts(
  accounts: AccountReadiness[],
): AccountReadiness[] {
  return accounts.filter(account =>
    DASHBOARD_AD_PLATFORMS.has(account.platform.toLowerCase()),
  )
}

export function buildSyncReadinessRows(
  readinessAccounts: AccountReadiness[],
  sessions: Record<number, SyncImportSession>,
): AccountRowModel[] {
  // Composite keys: external account ids are only unique per platform, so a
  // Google and a Meta account sharing a numeric id must not collide.
  const rows = new Map<string, AccountRowModel>()
  for (const account of dashboardScopedAccounts(readinessAccounts)) {
    const identity = accountDisplayIdentity(account)
    const rowKey = integrationAccountKey(
      account.platform,
      account.platform.toLowerCase() === 'chatgpt_ads'
        ? `#${account.id}`
        : account.account_id,
    )
    rows.set(rowKey, {
      key: rowKey,
      name: identity.primary,
      secondaryName: identity.secondary,
      platform: account.platform,
      readiness: account,
    })
  }
  for (const session of Object.values(sessions)) {
    for (const [accountId, sessionAccount] of Object.entries(
      session.accounts,
    )) {
      const rowKey = integrationAccountKey(
        session.platform,
        session.platform.toLowerCase() === 'chatgpt_ads' &&
          sessionAccount.internalId !== undefined
          ? `#${sessionAccount.internalId}`
          : accountId,
      )
      const existing = rows.get(rowKey)
      // A session-level terminal error (job-level SSE error frame, or a
      // client-side stream death signalled by the confirm promise) marks
      // every account the stream never finished as failed — otherwise the
      // rows would spin forever with no error surface.
      const terminalized =
        session.error !== undefined &&
        (sessionAccount.status === 'pending' ||
          sessionAccount.status === 'syncing')
      const identity = accountDisplayIdentity({
        account_id: accountId,
        name: sessionAccount.name,
        platform: existing?.platform ?? session.platform,
      })
      rows.set(rowKey, {
        key: rowKey,
        name: existing?.name ?? identity.primary,
        secondaryName: existing?.secondaryName ?? identity.secondary,
        platform: existing?.platform ?? session.platform,
        readiness: existing?.readiness,
        sessionStatus: terminalized ? 'error' : sessionAccount.status,
        campaignCount: sessionAccount.campaignCount,
        bindingError: session.bindingError ?? existing?.bindingError,
      })
    }
  }
  return Array.from(rows.values())
}

export function rowVisualState(row: AccountRowModel): ReadinessVisualState {
  if (
    row.sessionStatus === 'error' ||
    (row.readiness && aggregateAccountStatus(row.readiness) === 'attention')
  ) {
    return 'attention'
  }
  if (
    row.sessionStatus === 'pending' ||
    row.sessionStatus === 'syncing' ||
    !row.readiness
  ) {
    return 'syncing'
  }
  return aggregateAccountStatus(row.readiness)
}

/**
 * What the Sync Widget's pill should say, as data rather than copy.
 *
 * The cases are mutually exclusive and ordered: an import in flight outranks a
 * background sync. The `idle` case exists because the original implementation
 * had no such branch and fell through to the syncing label with a count of
 * zero — rendering "Syncing 0 account(s)…" next to a spinner that never
 * stopped.
 */
export type SyncPillState =
  | { kind: 'importing'; count: number }
  | { kind: 'syncing'; count: number }
  | { kind: 'idle' }

/**
 * Deliberately has no `attention` case. Accounts needing the user to act are
 * the header's to report; if the widget announced them too, the same fact would
 * be shouted from two places — the very thing this pair of surfaces was split
 * to avoid. It also used to outrank the import label, so an unrelated broken
 * account would hide the progress of an import that was actually running.
 *
 * A session is counted while it is merely retained (`reconciling`), not only
 * while `active`: the store keeps a finished import around until allocation and
 * cache settlement land, and its rows can already read as complete. Ignoring
 * that window made the widget flip to a green "all synced" before the import
 * had actually finished.
 */
export function syncPillState(input: {
  hasPendingSessions: boolean
  importingCount: number
  syncingCount: number
}): SyncPillState {
  if (input.hasPendingSessions) {
    return { kind: 'importing', count: input.importingCount }
  }
  if (input.syncingCount > 0) {
    return { kind: 'syncing', count: input.syncingCount }
  }
  return { kind: 'idle' }
}

/** "Google Ads: Acme Corp" — used by the header's attention popover. */
export function syncReadinessRowLabel(row: AccountRowModel): string {
  const platformLabel =
    PLATFORM_LABEL[row.platform.toLowerCase()] ?? row.platform
  return `${platformLabel}: ${row.secondaryName ?? row.name}`
}

export type SyncReadinessIssueMessageKey =
  | 'errorGuidance'
  | 'issues.reconnect_required'
  | 'issues.permission_required'
  | 'issues.google_permission_required'
  | 'issues.account_disabled'
  | 'issues.rate_limited'
  | 'issues.provider_unavailable'
  | 'issues.configuration_required'
  | 'issues.sync_stalled'
  | 'issues.sync_failed'
  | 'issues.recent_data_mismatch'

/** Google Ads help: granting a user access to an account. */
export const GOOGLE_ADS_ACCESS_HELP_URL =
  'https://support.google.com/google-ads/answer/6372672'

export type SyncReadinessIssueAction =
  | { kind: 'reconnect'; href: string }
  | { kind: 'configure'; href: string }
  | { kind: 'retry'; account: AccountReadiness }
  /** Fix lives on the provider side; an external help link is all we offer. */
  | { kind: 'grant_access'; href: string }
  /** Nothing the customer can do in Soku; the scheduled probe resumes sync. */
  | { kind: 'wait' }

export interface SyncReadinessIssuePresentation {
  messageKey: SyncReadinessIssueMessageKey
  action: SyncReadinessIssueAction | null
}

const ISSUE_MESSAGE_KEYS: Record<string, SyncReadinessIssueMessageKey> = {
  reconnect_required: 'issues.reconnect_required',
  permission_required: 'issues.permission_required',
  rate_limited: 'issues.rate_limited',
  provider_unavailable: 'issues.provider_unavailable',
  configuration_required: 'issues.configuration_required',
  sync_stalled: 'issues.sync_stalled',
  sync_failed: 'issues.sync_failed',
  recent_data_mismatch: 'issues.recent_data_mismatch',
  account_disabled: 'issues.account_disabled',
}

/**
 * Shared issue copy/action model for the Dashboard Header and Sync Widget.
 *
 * Configuration issues currently come only from ChatGPT Ads CAPI readiness,
 * whose established recovery entry is the settings route with
 * `guide=chatgpt-ads-tracking`. Reconnect continues to use the first-level
 * Integrations hub, where the platform's Manage dialog owns credential
 * recovery.
 */
export function syncReadinessIssuePresentation(args: {
  row: AccountRowModel
  orgSlug: string
  brandSlug: string
}): SyncReadinessIssuePresentation | null {
  const account = args.row.readiness
  const issue = account?.issue
  if (!account || !issue) {
    return rowVisualState(args.row) === 'attention'
      ? { messageKey: 'errorGuidance', action: null }
      : null
  }

  const messageKey = ISSUE_MESSAGE_KEYS[issue.code] ?? 'issues.sync_failed'
  if (issue.action === 'grant_access') {
    // Same code as the Meta case (permission_required) but the copy must not
    // suggest reconnecting: the authorized Google user simply lacks access.
    return {
      messageKey: 'issues.google_permission_required',
      action: { kind: 'grant_access', href: GOOGLE_ADS_ACCESS_HELP_URL },
    }
  }
  if (issue.action === 'wait') {
    return { messageKey, action: { kind: 'wait' } }
  }
  if (issue.action === 'reconnect') {
    return {
      messageKey,
      action: {
        kind: 'reconnect',
        href: buildBrandUrl(args.orgSlug, args.brandSlug, 'integrations'),
      },
    }
  }
  if (issue.action === 'configure') {
    return {
      messageKey,
      action: {
        kind: 'configure',
        href: `${buildBrandUrl(args.orgSlug, args.brandSlug, 'settings/integrations')}?guide=chatgpt-ads-tracking`,
      },
    }
  }
  return { messageKey, action: { kind: 'retry', account } }
}
